import { Link, useNavigate } from 'react-router-dom';
import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import TimePeriodSelector from './TimePeriodSelector';
import { TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import * as FormatFunctions from './FormatFunctions';
import { apiUrl } from './config';
import useUserStore from './UserStore';

function InvoiceTable() {
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customersById, setCustomersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const user = useUserStore((state) => state.user);
  const userEmail = user?.email
    || user?.user?.email
    || user?.profile?.email
    || user?.authResponse?.profile?.email
    || '';

  const rawUserRoles = [
    ...(Array.isArray(user?.roles) ? user.roles : []),
    ...(Array.isArray(user?.user?.roles) ? user.user.roles : []),
    ...(Array.isArray(user?.profile?.roles) ? user.profile.roles : []),
    ...(Array.isArray(user?.profileinfo?.roles) ? user.profileinfo.roles : []),
    ...(Array.isArray(user?.authResponse?.profile?.roles) ? user.authResponse.profile.roles : []),
  ];

  const userRoles = rawUserRoles
    .map((role) => {
      if (typeof role === 'string') {
        return role;
      }
      if (role && typeof role === 'object') {
        return role.name || role.role || role.value || '';
      }
      return '';
    })
    .filter(Boolean);

  const isAdmin = userRoles.some((role) => String(role || '').trim().toLowerCase() === 'admin');

  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setFromDate(firstDayOfMonth.toISOString().split('T')[0]);
    setToDate(lastDayOfMonth.toISOString().split('T')[0]);
  }, []);

  const fetchInvoices = async (page = 1, limit = 10) => {
    try {
      setLoading(true);

      if (!isAdmin && !userEmail) {
        setInvoices([]);
        setPagination({});
        setError('Unable to determine logged-in user email for invoice filtering.');
        return;
      }

      const queryParams = new URLSearchParams({
        page: String(page),
        page_size: String(limit),
      });

      if (fromDate) {
        queryParams.set('from_date', fromDate);
      }

      if (toDate) {
        queryParams.set('to_date', toDate);
      }

      if (!isAdmin && userEmail) {
        queryParams.set('sales_rep_email', userEmail);
      }

      const response = await fetch(apiUrl(`/invoice/all?${queryParams.toString()}`));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const invoiceData = await response.json();
      const fetchedInvoices = Array.isArray(invoiceData?.data) ? invoiceData.data : [];
      const normalizedUserEmail = (userEmail || '').toString().trim().toLowerCase();

      const scopedInvoices = (!isAdmin && normalizedUserEmail)
        ? fetchedInvoices.filter((invoice) => {
            const salesRepCandidate = (
              invoice?.sales_rep_email
              || invoice?.salesRepEmail
              || ''
            ).toString().trim().toLowerCase();
            return salesRepCandidate === normalizedUserEmail;
          })
        : fetchedInvoices;

      setInvoices(scopedInvoices);
      setPagination(invoiceData.pagination || {});
      setError(null);
    } catch (fetchError) {
      console.error('Error fetching invoices:', fetchError);
      setError('Failed to load invoices. Please check if the server is running.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomersById = async () => {
    try {
      const pageSize = 500;
      let page = 1;
      let totalPages = 1;
      const allCustomers = [];

      do {
        const response = await fetch(apiUrl(`/customer/all?page=${page}&page_size=${pageSize}`));
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const customerData = await response.json();
        const pageCustomers = Array.isArray(customerData?.data) ? customerData.data : [];
        allCustomers.push(...pageCustomers);
        totalPages = customerData?.pagination?.total_pages || 1;
        page += 1;
      } while (page <= totalPages);

      const customerMap = allCustomers.reduce((acc, customer) => {
        const customerKey = (customer?._id || customer?.id || customer?.customer_id || '').toString();
        if (customerKey) {
          acc[customerKey] = customer;
        }
        return acc;
      }, {});

      setCustomersById(customerMap);
    } catch (customerError) {
      console.error('Error fetching customers for invoice mapping:', customerError);
      setCustomersById({});
    }
  };

  useEffect(() => {
    fetchInvoices(pagination.current_page || 1, pagination.page_size || 10);
  }, [pagination.current_page, pagination.page_size, isAdmin, userEmail, selectedPeriod, fromDate, toDate]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  useEffect(() => {
    fetchCustomersById();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.current_page) {
      fetchInvoices(newPage, pagination.page_size);
      setCheckedItems([]);
    }
  };

  const handleItemsPerPageChange = (newLimit) => {
    fetchInvoices(1, newLimit);
    setCheckedItems([]);
  };

  const deleteInvoices = async () => {
    if (checkedItems.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${checkedItems.length} invoice(s)?`)) {
      try {
        const deletePromises = checkedItems.map((invoiceId) =>
          fetch(apiUrl('/invoice/delete'), {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id: invoiceId }),
            mode: 'cors',
          })
        );

        const responses = await Promise.all(deletePromises);
        responses.forEach((response, index) => {
          if (!response.ok) {
            throw new Error(`Failed to delete invoice ${checkedItems[index]}: ${response.status}`);
          }
        });

        fetchInvoices(pagination.current_page || 1, pagination.page_size || 10);
        setCheckedItems([]);
      } catch (deleteError) {
        console.error('Error deleting invoices:', deleteError);
        alert(`Failed to delete invoices: ${deleteError.message}`);
      }
    }
  };

  const handleCheckboxChange = (id) => {
    setCheckedItems((currentCheckedItems) => {
      if (currentCheckedItems.includes(id)) {
        return currentCheckedItems.filter((item) => item !== id);
      }
      return [...currentCheckedItems, id];
    });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const { current_page, total_pages } = pagination;

    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoiceview/${invoiceId}`);
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/invoiceedit/${invoiceId}`);
  };

  if (loading) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Invoices" />
        <div className="m-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading invoices...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Invoices" />
        <div className="m-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
      <Titlebar title="Invoices" />

      <div className="m-3 px-4 sm:px-6 lg:px-8">
        {/* Time Period Selector */}
        <div className="flex items-center justify-between mb-6">
        <TimePeriodSelector 
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
        />
        </div>

        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            {invoices && invoices.length > 0 && (
              <p className="mt-2 text-sm text-gray-700">
                Showing {invoices.length} of {pagination.total_count} invoices
              </p>
            )}
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Link
              to="/invoiceadd"
              className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Add Invoice
            </Link>
          </div>
        </div>

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              {invoices && Array.isArray(invoices) ? (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="w-1.5 px-3 py-3 text-sm font-semibold text-gray-900 sm:table-cell">
                        <button
                          type="button"
                          onClick={deleteInvoices}
                          disabled={!checkedItems.length > 0}
                          className="rounded-full bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                        >
                          <TrashIcon aria-hidden="true" className="h-5 w-5" />
                        </button>
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Campaign</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Due Date</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Impressions</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount Due</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Total Value</th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {invoices.map((invoice) => {
                      const invoiceId = invoice.id || invoice._id || invoice.invoice_id;
                      const invoiceCustomerId = (invoice.customer_id || invoice.customerId || '').toString();
                      const customer = customersById[invoiceCustomerId] || null;
                      const invoiceStatus = (invoice.status || '').toString().trim().toLowerCase();
                      const customerName = customer?.name || '-';
                      const customerAddressLine1 = [customer?.billing_address_1, customer?.billing_address_2]
                        .filter(Boolean)
                        .join(', ');
                      const customerAddressLine2 = [customer?.billing_city, customer?.billing_state, customer?.billing_zip_code]
                        .filter(Boolean)
                        .join(' ');
                      const customerAddress = [customerAddressLine1, customerAddressLine2].filter(Boolean).join(' • ') || '-';
                      const statusClass = invoiceStatus === 'paid'
                        ? 'bg-green-50 text-green-700 ring-green-600/20'
                        : invoiceStatus === 'past_due'
                          ? 'bg-red-50 text-red-700 ring-red-600/20'
                          : 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';

                      return (
                        <tr key={invoiceId}>
                          <td className="w-1.5 px-3 py-5 text-sm font-medium text-gray-900 sm:table-cell">
                            <input
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              type="checkbox"
                              checked={checkedItems.includes(invoiceId)}
                              onChange={() => handleCheckboxChange(invoiceId)}
                            />
                          </td>
                          <td className="py-5 pl-4 pr-3 text-sm sm:pl-0">
                            <div className="font-medium text-gray-900">{customerName}</div>
                            <div className="mt-1 text-gray-500 break-words">{customerAddress}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{invoice.order_campaign || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            {FormatFunctions.formatDate(invoice.due_date)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm">
                            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusClass}`}>
                              {invoice.status || '-'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{invoice.total_impressions ?? 0}</td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{FormatFunctions.formatCurrency(invoice.amount_due)}</td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{FormatFunctions.formatCurrency(invoice.total_value)}</td>
                          <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                            <button
                              onClick={() => handleViewInvoice(invoiceId)}
                              className="text-indigo-600 hover:text-indigo-900 mr-2"
                            >
                              View<span className="sr-only">, Invoice {invoiceId}</span>
                            </button>
                            &nbsp; | &nbsp;
                            <button
                              onClick={() => handleEditInvoice(invoiceId)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit<span className="sr-only">, Invoice {invoiceId}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    {invoices === null ? 'No invoices data available.' : 'Unable to load invoices.'}
                  </div>
                </div>
              )}

              {invoices && Array.isArray(invoices) && invoices.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-500">No invoices found.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {invoices && invoices.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_previous || pagination.current_page <= 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next || pagination.current_page >= pagination.total_pages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>

            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((pagination.current_page - 1) * pagination.page_size) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.current_page * pagination.page_size, pagination.total_count)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total_count}</span> results
                </p>

                <div className="flex items-center space-x-2">
                  <label htmlFor="items-per-page" className="text-sm text-gray-700">Show:</label>
                  <select
                    id="items-per-page"
                    value={pagination.page_size || 10}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={!pagination.has_previous || pagination.current_page <= 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNumber === pagination.current_page
                          ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ))}

                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={!pagination.has_next || pagination.current_page >= pagination.total_pages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default InvoiceTable;